import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';

import Container from '../../layout/container/container';
import { StyledExhibitionContainer, StyledExhibitionStations } from './exhibitionContainer.style';

import TypoHeadline from '../../molecules/typo-headline/typoHeadline';
import ArticleTeaser from '../../molecules/article-teaser/articleTeaser';
import Headline from '../../atoms/headline/headline';
import Copy from '../../atoms/copy/copy';

// Arrow Icons for paths between stations
import ArrowStraight from '../../icons/arrowStraight.svg';
import ArrowStraightSmall from '../../icons/arrowStraightSmall.svg';
import ArrowCorner from '../../icons/arrowCorner.svg';
import ArrowStairs from '../../icons/arrowStairs.svg';
import ArrowStairsSmall from '../../icons/arrowStairsSmall.svg';
import TokenIconSmall from '../../icons/tokenIconSmall.svg';

import FullscreenMedia from '../../molecules/fullscreen-media/fullscreenMedia';
import SliderSkeleton from '../slider/slider.skeleton';
import Quote from '../../molecules/quote/quote';
import DidYouKnowDivider from '../../molecules/did-you-know-divider/didYouKnowDivider';
import { getColor } from '../../../stylesheets/tools';
import Outro from '../outro/outro';

const Slider = dynamic({
  loader: () => import('../slider/slider'),
  loading: () => <SliderSkeleton />,
  ssr: false
});

/**
 * Gets the correct color for a thinkingSpace.
 * @param {string} thinkingSpace ThinkingSpace name
 * @returns {string} The correct thinkingSpace color
 */
const getThinkingSpaceColor = thinkingSpace => {
  if (thinkingSpace === 'human' || thinkingSpace === 'mensch') {
    return 'blue';
  }
  if (thinkingSpace === 'technology' || thinkingSpace === 'technik') {
    return 'grey';
  }
  if (thinkingSpace === 'nature' || thinkingSpace === 'natur') {
    return 'green';
  }
  return 'default';
};

const getThinkingSpaceTheme = thinkingSpace => {
  if (thinkingSpace === 'human' || thinkingSpace === 'mensch') {
    return getColor('mfBlue');
  }
  if (thinkingSpace === 'technology' || thinkingSpace === 'technik') {
    return getColor('mfGrey');
  }
  if (thinkingSpace === 'nature' || thinkingSpace === 'natur') {
    return getColor('mfGreen');
  }
  return '';
};

/**
 * While the alignment of the thinkingSpace is perfectly fine for the
 * first cluster. Following clusters of the same thinkingSpace need
 * an alignment based on the length of the previous cluster.
 * @param {number} prevStationLength Length of stations
 * @param {string} alignment ThinkingSpace alignment
 * @returns {string} Cluster align
 */
const getClusterAlignment = (prevStationLength, alignment) => {
  if (prevStationLength % 2 === 1) {
    if (alignment === 'left') {
      return 'right';
    }

    if (alignment === 'right') {
      return 'left';
    }
  }
  return alignment;
};

/**
 * Renders the station arrow. The station arrow indicates
 * the flow of the stations (right, left)
 * @param {number} stationIndex Index of the station
 * @param {number} clusterIndex Index of the cluster
 * @param {array} stationsLength Length of the stations
 * @returns {Element} Station arrow
 */
const renderStationArrow = (stationIndex, clusterIndex, stationsLength) => {
  const classes = 'exhibitionContainer__info-icon';

  if (
    (stationsLength === 3 && stationIndex === stationsLength - 1) ||
    (stationIndex === 0 && clusterIndex !== 0)
  ) {
    return <ArrowStairs className={classes} />;
  }

  if (stationIndex !== 0) {
    return (
      <div>
        <ArrowCorner className={`${classes} no-mobile`} />
        <ArrowStairs className={`${classes} only-mobile`} />
      </div>
    );
  }

  return <ArrowStraight className={classes} />;
};

const getModule = (module, stationAlignment) => {
  switch (module.type) {
    case 'articleTeaser':
      return (
        <Container>
          <ArticleTeaser alignment={stationAlignment} size="m" {...module.data} />
        </Container>
      );
    case 'embeddedVideo':
      return (
        <Container>
          <FullscreenMedia
            alignment={stationAlignment}
            customTheme={getThinkingSpaceTheme(module.thinkingSpace)}
            type="video"
            hoster={module.hoster}
            videoId={module.videoId}
            previewImage={module.previewImage}
            {...module}
          />
        </Container>
      );
    case 'image': {
      return (
        <Container>
          <FullscreenMedia
            alignment={stationAlignment}
            customTheme={getThinkingSpaceTheme(module.data.thinkingSpace)}
            type="fullwidthImage"
            file={module.data.file}
          />
        </Container>
      );
    }
    case 'gallery':
      return (
        <Container>
          <Slider {...module} />
        </Container>
      );
    case 'quote':
      return (
        <Container>
          <Quote color={getThinkingSpaceTheme(module.thinkingSpace)} {...module} />
        </Container>
      );
    case 'didYouKnow':
      return (
        <DidYouKnowDivider
          color={getThinkingSpaceColor(module.data.thinkingSpace)}
          {...module.data}
        />
      );
    case 'outro':
      return (
        <Container>
          <Outro
            video={
              module.data.videoId && module.data.hoster
                ? { videoId: module.data.videoId, hoster: module.data.hoster }
                : null
            }
            teaserImage={module.data.teaserImage}
            headline={module.data.headline}
            subline={module.data.subline}
            copy={module.data.copy}
          />
        </Container>
      );
    default:
      return null;
  }
};
const renderStationModule = (module, stationSize, stationAlignment, stationIndex) => {
  const ArrowIcon = stationSize === 'm' ? ArrowStraightSmall : ArrowStairsSmall;
  return (
    <>
      <div className={`exhibitionContainer__module exhibitionContainer__module--${module.type}`}>
        <div className="exhibitionContainer__module-type">
          {getModule(module, stationAlignment)}
        </div>
        {module.type !== 'didYouKnow' && (
          <ArrowIcon
            className={`exhibitionContainer__last-arrow-handler exhibitionContainer__module-end-icon ${stationIndex} ${
              (stationIndex + 1) % 2 === 0
                ? `exhibitionContainer__module-even ${`exhibitionContainer__module-${stationAlignment}`}`
                : `exhibitionContainer__module-odd ${`exhibitionContainer__module-${stationAlignment}`}`
            }`}
          />
        )}
      </div>
    </>
  );
};

/**
 * Renders the stations of a cluster.
 * @param {number} clusterIndex Index of the cluster
 * @param {object} prevCluster Previous cluster
 * @param {array} stations Stations of a cluster
 * @param {string} alignment Thinkingspace alignment
 * @param {string} thinkingSpace ThinkingSpace name as slug
 * @returns {Element} Cluster stations
 */
const renderClusterStations = (clusterIndex, prevCluster, stations, alignment, thinkingSpace) => (
  <StyledExhibitionStations
    stationLength={stations.length}
    clusterIndex={clusterIndex}
    alignment={
      clusterIndex !== 0
        ? getClusterAlignment(prevCluster.data.stations.length, alignment)
        : alignment
    }
  >
    <Container className="exhibitionContainer__stations">
      {stations.map((station, index) => {
        const stationSize = index === stations.length - 1 && stations.length === 3 ? 'm' : 's';
        const stationAlignment =
          index === 0 && clusterIndex !== 0
            ? getClusterAlignment(prevCluster.data.stations.length, alignment)
            : [alignment, alignment === 'left' ? 'right' : 'left'][index % 2];
        const ArrowIcon = stationSize === 'm' ? ArrowStraightSmall : ArrowStairsSmall;

        return (
          <>
            <div
              className={`exhibitionContainer__teaser-wrapper exhibitionContainer__teaser-wrapper--${stationSize} ${
                station.data.image
                  ? 'exhibitionContainer__teaser-wrapper-with-img'
                  : 'exhibitionContainer__teaser-no-img'
              }`}
            >
              <div className="exhibitionContainer__teaser-info">
                <label
                  htmlFor={`trigger-${index}-${station.data.timestamp}`}
                  className={`exhibitionContainer__teaser-info-wrapper ${
                    station.data.timestamp
                      ? 'exhibitionContainer__teaser-info-wrapper-has-timestamp'
                      : 'exhibitionContainer__teaser-info-wrapper-no-timestamp'
                  }`}
                >
                  {renderStationArrow(index, clusterIndex, stations.length)}
                  {station.data.timestamp && (
                    <input
                      id={`trigger-${index}-${station.data.timestamp}`}
                      type="checkbox"
                      className="exhibitionContainer__info-trigger"
                    />
                  )}
                  <div className="exhibitionContainer__info-count">
                    <TokenIconSmall />
                    <Headline subline tag="span">{`#${station.data.count}`}</Headline>
                    {station.data.timestamp && (
                      <>
                        <Copy small tag="span">
                          {station.data.timestamp && station.data.timestamp.split(' ')[0]}
                        </Copy>
                        <Copy small tag="span">
                          {station.data.timestamp && station.data.timestamp.split(' ')[1]}
                        </Copy>
                      </>
                    )}
                  </div>
                </label>
              </div>
              <ArticleTeaser
                {...station.data}
                category={thinkingSpace}
                fullWidth
                alignment={stationAlignment}
                size={stationSize}
                key={`${station.data.slug}-${station.data.id ? station.data.id : index}`}
                className="exhibitionContainer__teaser"
              />

              {index === stations.length - 1 && (
                <ArrowIcon className="exhibitionContainer__end-icon exhibitionContainer__last-arrow-handler" />
              )}
            </div>
            {station.data.modules &&
              station.data.modules.map(module => {
                return renderStationModule(module, stationSize, stationAlignment, index);
              })}
          </>
        );
      })}
    </Container>
  </StyledExhibitionStations>
);

/**
 * Renders the clusters of a thinkingSpace. The cluster headline for the first
 * cluster is a combined headline, that is also the thinkingSpace headline. Following
 * clusters have their own headline.
 * @param {array} clusters Clusters of a thinkingSpace
 * @param {string} thinkingSpace ThinkingSpace name as slug
 * @param {string} alignment Thinkingspace alignment
 * @returns {Element} Cluster of a thinkingSpace
 */
const renderExhibitionClusters = (clusters, thinkingSpace, alignment) =>
  clusters.map((cluster, index) => (
    <div className="exhibitionContainer__cluster" key={cluster.data.slug}>
      {index !== 0 && (
        <Container>
          <TypoHeadline
            headline={cluster.data.headline}
            alignment="center"
            highlight={false}
            color={getThinkingSpaceColor(thinkingSpace)}
          />
        </Container>
      )}
      {renderClusterStations(
        index,
        clusters[index - 1],
        cluster.data.stations,
        alignment,
        thinkingSpace
      )}
    </div>
  ));

/**
 * Renders the thinkingSpace based on the given data
 * @param {number} index Mostly used for map key attribute
 * @param {object} data ThinkingSpace data (didYouKnowDivider, first cluster headline, clusters)
 * @param {array} alignment Simply left or right
 * @returns {Element} Either the didYouKnowDivider or the thinkingSpace
 */
const renderExhibitionThinkingSpace = (index, data, alignment) => ({
  thinkingSpace: (
    <div className="exhibitionContainer__thinkingSpace" key={`${data.slug}-${index}`}>
      <Container>
        <TypoHeadline
          headline={data.combinedHeadline}
          color={getThinkingSpaceColor(data.slug)}
          alignment={alignment}
        />
      </Container>
      {data.cluster && renderExhibitionClusters(data.cluster, data.slug, alignment)}
    </div>
  )
});

const ExhibitionContainer = ({ data, hidePaginationArrow }) => {
  let alignments = ['right', 'left'];
  let alignment;

  useEffect(() => {
    if (hidePaginationArrow) {
      const lastItem = Array.from(
        document.querySelectorAll('.exhibitionContainer__last-arrow-handler')
      ).pop();

      if (lastItem !== undefined) {
        lastItem.style.display = 'none';
      }
    }
  }, []);

  return (
    <StyledExhibitionContainer>
      {data.map((entry, index) => {
        if (
          index !== 0 &&
          data[index - 1].type === 'thinkingSpace' &&
          entry.type === 'didYouKnow'
        ) {
          alignments = alignments.reverse();
        }

        alignment = alignments[index % 2];

        if (entry.type === 'quote') {
          return renderStationModule(entry, 'm', alignment, index);
        }
        if (entry.type === 'didYouKnow') {
          return renderStationModule(entry, 'l', alignment, index);
        }
        if (entry.type === 'image') {
          return renderStationModule(entry, 'l', alignment, index);
        }
        if (entry.type === 'gallery') {
          return renderStationModule(
            {
              gallery: entry.gallery,
              type: 'gallery'
            },
            'l',
            alignment,
            index
          );
        }
        if (
          entry.type === 'embeddedVideo' ||
          entry.type === 'articleTeaser' ||
          entry.type === 'outro'
        ) {
          return renderStationModule(entry, '', alignment, index);
        }
        return renderExhibitionThinkingSpace(index, entry.data, alignment)[entry.type];
      })}
    </StyledExhibitionContainer>
  );
};

ExhibitionContainer.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  hidePaginationArrow: PropTypes.bool.isRequired
};

export default ExhibitionContainer;
